import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * Hover with intent, for the expand-on-hover rail.
 *
 * The two delays are deliberately asymmetric, because opening and closing fail
 * in opposite directions:
 *
 * - Opening needs a *short* delay. Without one, dragging the pointer across the
 *   screen throws the panel open every time it clips the left edge. ~90ms is
 *   long enough to ignore a pass-through and short enough to feel immediate
 *   when you actually aimed at it.
 *
 * - Closing needs a *long* one. The old code used a flat 120ms for both, so
 *   reaching diagonally for a submenu — briefly leaving the panel's bounds on
 *   the way — collapsed it out from under the pointer mid-click. That is the
 *   "clicking doesn't work" symptom. ~280ms comfortably covers a correction.
 *
 * `openNow`/`closeNow` bypass the delays for focus and Escape, where the intent
 * is already unambiguous.
 */
export const useHoverIntent = ({ openDelay = 90, closeDelay = 280 } = {}) => {
  const [isOpen, setOpen] = useState(false);
  const timerRef = useRef(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const open = useCallback(() => {
    clear();
    timerRef.current = setTimeout(() => setOpen(true), openDelay);
  }, [clear, openDelay]);

  const close = useCallback(() => {
    clear();
    timerRef.current = setTimeout(() => setOpen(false), closeDelay);
  }, [clear, closeDelay]);

  const openNow = useCallback(() => {
    clear();
    setOpen(true);
  }, [clear]);

  const closeNow = useCallback(() => {
    clear();
    setOpen(false);
  }, [clear]);

  useEffect(() => clear, [clear]);

  return { isOpen, open, close, openNow, closeNow };
};

export default useHoverIntent;
